import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { FounderAvatarRing, isFounderUser } from "@/components/FounderAvatarRing";
import CreatorBadge from "@/components/CreatorBadge";
import EditProfileModal from "@/components/EditProfileModal";
import FollowListModal from "@/components/FollowListModal";
import { useAuth } from "@/hooks/useAuth";
import { getQueryFn } from "@/lib/queryClient";
import { formatCount } from "@/lib/utils";
import type { User } from "@shared/schema";
import { Link as LinkIcon, ArrowUpRight } from "lucide-react";

// Whitelist of routes where the rail is allowed. Any route not listed
// (including the bare /:username catch-all for user profiles) hides it.
const SHOW_RAIL_ON: RegExp[] = [
  /^\/search$/,
  /^\/discover$/,
  /^\/bookmarks$/,
  /^\/activity$/,
  /^\/analytics$/,
  /^\/hashtag\/[^/]+$/,
  /^\/post\/[^/]+$/,
];

export function shouldShowProfileRail(pathname: string): boolean {
  return SHOW_RAIL_ON.some((re) => re.test(pathname));
}

export default function ProfileSidebar() {
  const [location] = useLocation();
  const { user: authUser, isAuthenticated } = useAuth();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [followListTab, setFollowListTab] = useState<"followers" | "following" | null>(null);

  const { data: fullUser } = useQuery<User>({
    queryKey: [`/api/users/${authUser?.id}`],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!authUser?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (!isAuthenticated || !authUser) return null;
  if (!shouldShowProfileRail(location)) return null;

  const profileUser = (fullUser ?? (authUser as unknown as User)) as User;
  const fallbackInitial = (profileUser.displayName || profileUser.username || "?").slice(0, 1).toUpperCase();
  const hasBio = !!profileUser.bio?.trim();
  const hasAvatar = !!profileUser.profileImageUrl;
  const needsProfileNudge = !hasBio || !hasAvatar;
  const websiteHref = profileUser.websiteUrl
    ? (profileUser.websiteUrl.startsWith("http") ? profileUser.websiteUrl : `https://${profileUser.websiteUrl}`)
    : null;
  const websiteLabel = profileUser.websiteUrl?.replace(/^https?:\/\//, "") ?? "";

  return (
    <>
      <aside
        className="profile-rail-shell hidden xl:flex flex-col w-[320px] m-2 bg-(--butter-bg-elevated) shrink-0 relative z-10 rounded-2xl border border-(--scroll-border) overflow-hidden butter-shadow-panel"
        aria-label="Your profile"
      >
        <div className="flex-1 overflow-y-auto scrollbar-hide px-5 pt-6 pb-4">
          {/* Identity */}
          <div className="flex flex-col items-start gap-3">
            <div className="shrink-0">
              <FounderAvatarRing isFounder={isFounderUser(profileUser.id)}>
                <Avatar className="h-16 w-16 border border-(--scroll-border)">
                  <AvatarImage src={profileUser.profileImageUrl ?? undefined} alt="" />
                  <AvatarFallback className="text-lg">{fallbackInitial}</AvatarFallback>
                </Avatar>
              </FounderAvatarRing>
            </div>
            <div className="min-w-0 w-full">
              <Link href="/profile" className="block">
                <h3 className="scroll-heading text-[17px] leading-tight tracking-tight text-(--scroll-text) flex items-center gap-1.5 flex-wrap">
                  <span className="truncate">{profileUser.displayName || profileUser.username}</span>
                  <CreatorBadge verified={!!profileUser.isVerified} accentColor={profileUser.accentColor} size="sm" />
                </h3>
                <p className="text-[13px] text-(--scroll-text-secondary) mt-0.5 truncate">
                  @{profileUser.username}
                </p>
              </Link>
            </div>
          </div>

          {/* Bio */}
          {hasBio && (
            <p className="mt-4 text-[13px] leading-relaxed text-(--scroll-text) line-clamp-3 whitespace-pre-wrap">
              {profileUser.bio}
            </p>
          )}

          {/* Website */}
          {websiteHref && (
            <a
              href={websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-(--scroll-accent) hover:underline"
            >
              <LinkIcon size={12} strokeWidth={2} />
              <span className="truncate">{websiteLabel}</span>
            </a>
          )}

          {/* Stats */}
          <div className="mt-4 flex items-center gap-4 text-[13px]">
            <button
              onClick={() => setFollowListTab("followers")}
              className="text-(--scroll-text) hover:underline"
            >
              <span className="font-semibold">{formatCount(profileUser.followersCount ?? 0)}</span>
              <span className="text-(--scroll-text-secondary) ml-1">followers</span>
            </button>
            <button
              onClick={() => setFollowListTab("following")}
              className="text-(--scroll-text) hover:underline"
            >
              <span className="font-semibold">{formatCount(profileUser.followingCount ?? 0)}</span>
              <span className="text-(--scroll-text-secondary) ml-1">following</span>
            </button>
          </div>

          {/* Primary action */}
          <Button
            variant="brand"
            size="compact"
            className="mt-5 w-full"
            onClick={() => setShowEditProfile(true)}
          >
            Edit profile
          </Button>

          {/* Complete-profile nudge */}
          {needsProfileNudge && (
            <div className="mt-4 rounded-lg border border-dashed border-(--scroll-border) bg-(--scroll-bg-secondary) px-3 py-2.5">
              <p className="text-[12px] text-(--scroll-text-secondary) leading-relaxed">
                {!hasAvatar && !hasBio
                  ? "Add an avatar and bio so people know you."
                  : !hasAvatar
                    ? "Add a profile photo so people recognize you."
                    : "Tell people about yourself with a short bio."}
              </p>
            </div>
          )}

          {/* Secondary link */}
          <Link
            href="/profile"
            className="mt-5 inline-flex items-center gap-1 text-[12px] font-medium text-(--scroll-text-tertiary) hover:text-(--scroll-text)"
          >
            View full profile
            <ArrowUpRight size={12} strokeWidth={2} />
          </Link>
        </div>
      </aside>

      {showEditProfile && (
        <EditProfileModal
          open={showEditProfile}
          onClose={() => setShowEditProfile(false)}
          user={profileUser}
        />
      )}
      {followListTab && profileUser.id && profileUser.username && (
        <FollowListModal
          isOpen={!!followListTab}
          onClose={() => setFollowListTab(null)}
          userId={profileUser.id}
          username={profileUser.username}
          initialTab={followListTab}
        />
      )}
    </>
  );
}
